import { lazy, Suspense } from 'react'

import { AdvancedSettingsPage } from '@capture/client/components/AdvancedSettings/AdvancedSettingsPage'
import { CommentsListOverlay } from '@capture/client/components/Album/CommentsListOverlay'
import { LoadingPage } from '@capture/client/components/Common/LoadingPage'
import { TakeoutPage } from '@capture/client/components/Takeout/TakeoutPage'
import UnsubscribePage from '@capture/client/components/Unsubscribe/index'
import type { NonIndexRouteObject } from 'react-router-dom'
import { createBrowserRouter, json, RouterProvider } from 'react-router-dom'
import { makeAlbumMount } from '@capture/client/components/Album/AlbumMount'
import { DocumentsPage } from '@capture/client/components/Documents/DocumentsPage'
import { EditAlbumPage } from '@capture/client/components/EditAlbum/EditAlbumPage'
import {
    AddToAlbumSelectionPage,
    MorePhotosSelectionPage,
} from '@capture/client/components/EditAlbum/PhotoSelectionPage'
import { ErrorPageMount } from '@capture/client/components/Info/ErrorPage'
import { PageNotFound } from '@capture/client/components/Info/PageNotFound'
import { LoginRequiredCopyAlbum } from '@capture/client/components/Info/RequireLoginCopyAlbum'
import { RequireLoginCreateNewAlbum } from '@capture/client/components/Info/RequireLoginCreateNewAlbum'
import { RequireLoginSubscribeToAlbum } from '@capture/client/components/Info/RequireLoginSubscribeToAlbum'
import { LoggedInDevicesPage } from '@capture/client/components/Settings/LoggedInDevicesPage'
import { SelectProfilePicturePage } from '@capture/client/components/Settings/SelectProfilePhotoPage'
import { TestFlagAdmin } from '@capture/client/components/Settings/TestFlagAdmin'
import { TrashAlbumsPage } from '@capture/client/components/Trash/TrashAlbumsPage'
import { TrashNavigationPage } from '@capture/client/components/Trash/TrashNavigationPage'
import { TrashPhotosPage } from '@capture/client/components/Trash/TrashPhotosPage'
import { RedirectToTimelineOrLogin } from '@capture/client/routing/RedirectOrLogin'
import { RoutePath } from '@capture/client/routing/routePath'
import { wrappedIn } from '@capture/client/utilities/hocComposition'
import { EmailSettingsPage } from '@capture/client/components/EmailSettings/EmailSettingsPage'
import { fetchAllUserFiles, fetchDefaultJobID } from '@capture/client/API/job'
import { CarouselViewEntered } from '@capture/client/state/carouselViewer/actions'
import { b64ToUuid } from '@capture/client/utilities/uuid'
import { isSessionAuthorized } from '@capture/client/API/externals'
import { PrivacyPage } from '../components/DataProtection/PrivacyPage'
import { AlbumFileAsShareMount } from '../components/ShareReceiver/AlbumFileAsShare'
import { RequireLoginCopyShare } from '../components/ShareReceiver/RequireLoginCopyShare'
import { ShareMountUnpackedUUID } from '../components/ShareReceiver/ShareContainer'
import { getStore } from '../store.client'
import { LoggedInApp, MaybeLoggedInApp } from './App'

// lazy load some routes
const AlbumCarouselViewPage = lazy(() =>
    import('@capture/client/components/CarouselView/CarouselViewPage').then(
        (module) => ({
            default: module.AlbumCarouselViewPage,
        }),
    ),
)

const AlbumListPage = lazy(() =>
    import('@capture/client/components/AlbumList/AlbumListPage').then(
        (module) => ({
            default: module.AlbumListPage,
        }),
    ),
)

const AlbumReceiverPage = lazy(() =>
    import('@capture/client/components/Album/AlbumReceiverPage').then(
        (module) => ({
            default: module.AlbumReceiverPage,
        }),
    ),
)

const TimelineCarouselViewPage = lazy(() =>
    import('@capture/client/components/CarouselView/CarouselViewPage').then(
        (module) => ({
            default: module.TimelineCarouselViewPage,
        }),
    ),
)

const TimelinePage = lazy(() =>
    import('@capture/client/components/Timeline/TimelinePage').then(
        (module) => ({
            default: module.TimelinePage,
        }),
    ),
)

const SettingsStoragePage = lazy(() =>
    import('../routes/settings.storage').then((module) => ({
        default: module.SettingsStoragePage,
    })),
)

const SettingsPage = lazy(() =>
    import('@capture/client/components/Settings/SettingsPage').then(
        (module) => ({
            default: module.SettingsPage,
        }),
    ),
)

const AlbumMount = makeAlbumMount(AlbumReceiverPage) // TODO: New inner component

const loggedIn = wrappedIn(LoggedInApp)
const maybeLoggedIn = wrappedIn(MaybeLoggedInApp)

const LoggedInTimelinePage = loggedIn(TimelinePage)
const LoggedInTimelineCarouselViewPage = loggedIn(TimelineCarouselViewPage)
const LoggedInAlbumListPage = loggedIn(AlbumListPage)
const LoggedInAlbumMount = loggedIn(AlbumMount)

const LoggedInTrashNavigationPage = loggedIn(TrashNavigationPage)
const LoggedInTrashPhotosPage = loggedIn(TrashPhotosPage)
const LoggedInTrashAlbumsPage = loggedIn(TrashAlbumsPage)

const LoggedInMorePhotosSelectionPage = loggedIn(MorePhotosSelectionPage)
const LoggedInAddToAlbumSelectionPage = loggedIn(AddToAlbumSelectionPage)
const LoggedInEditAlbumPage = loggedIn(EditAlbumPage)
const LoggedInDocumentsPage = loggedIn(DocumentsPage)
const LoggedInSettingsPage = loggedIn(SettingsPage)
const LoggedInStoragePage = loggedIn(SettingsStoragePage)
const LoggedInPrivacyPage = loggedIn(PrivacyPage)
const LoggedInTakeoutPage = loggedIn(TakeoutPage)
const LoggedInLoggedInDevicesPage = loggedIn(LoggedInDevicesPage)
const LoggedInSelectProfilePicturePage = loggedIn(SelectProfilePicturePage)
const MaybeLoggedInAlbumFileAsShareMount = maybeLoggedIn(AlbumFileAsShareMount)
const MaybeLoggedInAlbumMount = maybeLoggedIn(AlbumMount)
const MaybeLoggedInAlbumCarouselViewPage = maybeLoggedIn(AlbumCarouselViewPage)
const MaybeLoggedInRequireLoginCreateNewAlbum = maybeLoggedIn(
    RequireLoginCreateNewAlbum,
)
const MaybeLoggedInLoginRequiredCopyAlbum = maybeLoggedIn(
    LoginRequiredCopyAlbum,
)
const MaybeLoggedInRequireLoginSubscribeToAlbum = maybeLoggedIn(
    RequireLoginSubscribeToAlbum,
)
const MaybeLoggedInShareMountUnpackedUUID = maybeLoggedIn(
    ShareMountUnpackedUUID,
)
const MaybeLoggedInEmailSettings = maybeLoggedIn(EmailSettingsPage)
const MaybeLoggedInRequireLoginCopyShare = maybeLoggedIn(RequireLoginCopyShare)
const MaybeLoggedInCommentsListOverlay = maybeLoggedIn(CommentsListOverlay)
const MaybeLoggedInErrorPageMount = maybeLoggedIn(ErrorPageMount)
const MaybeLoggedInPageNotFound = maybeLoggedIn(PageNotFound)

const Unsubscribe = loggedIn(UnsubscribePage)

const LoggedInAdvancedSettings = loggedIn(AdvancedSettingsPage)

const timelineCarouselLoader: NonIndexRouteObject['loader'] = async ({
    params,
}) => {
    const { fileID } = params
    if (fileID === undefined) {
        throw new Error('Missing file ID')
    }

    if (!isSessionAuthorized()) {
        return json({}, { status: 401 })
    }

    const store = getStore()

    const timelineID = await fetchDefaultJobID(store.dispatch)

    if (timelineID === undefined) {
        throw new Error(
            'No timeline job ID could be found for the current user',
        )
    }

    void fetchAllUserFiles(store.dispatch, timelineID)

    store.dispatch(
        CarouselViewEntered({
            jobID: timelineID,
            fileID,
            sourcePage: 'TimelinePage',
        }),
    )

    return json({ timelineID }, { status: 200 })
}

const albumCarouselLoader: NonIndexRouteObject['loader'] = async ({
    params,
}) => {
    const { albumIDB64, fileID } = params
    if (albumIDB64 === undefined || fileID === undefined) {
        throw new Error('Missing albumIDB64 or file ID')
    }

    const albumID = b64ToUuid(albumIDB64)
    const store = getStore()

    store.dispatch(
        CarouselViewEntered({
            jobID: albumID,
            fileID: fileID,
            sourcePage: 'AlbumPage',
        }),
    )

    return null
}

const router = createBrowserRouter(
    [
        {
            path: '/',
            element: <RedirectToTimelineOrLogin />,
        },
        {
            path: RoutePath.Timeline,
            element: <LoggedInTimelinePage />,
        },
        {
            path: RoutePath.getTimelineCarouselPath(':fileID'),
            loader: timelineCarouselLoader,
            element: <LoggedInTimelineCarouselViewPage />,
        },
        {
            path: RoutePath.Albums,
            element: <LoggedInAlbumListPage />,
        },
        {
            path: RoutePath.getLoggedInAlbumPath(':albumIDB64'),
            element: <LoggedInAlbumMount />,
        },
        {
            path: RoutePath.InAppTrashNavigation,
            element: <LoggedInTrashNavigationPage />,
        },
        {
            path: RoutePath.TrashNavigation,
            element: <LoggedInTrashNavigationPage />,
        },
        {
            path: RoutePath.getInAppTrashPhotosPath(),
            element: <LoggedInTrashPhotosPage />,
        },
        {
            path: RoutePath.TrashPhotos,
            element: <LoggedInTrashPhotosPage />,
        },
        {
            path: RoutePath.getInAppTrashAlbumsPath(),
            element: <LoggedInTrashAlbumsPage />,
        },
        {
            path: RoutePath.TrashAlbums,
            element: <LoggedInTrashAlbumsPage />,
        },
        {
            path: RoutePath.morePhotosCreateAlbum(':albumIDB64'),
            element: <LoggedInMorePhotosSelectionPage />,
        },
        {
            path: RoutePath.morePhotosAlbum(':albumIDB64'),
            element: <LoggedInAddToAlbumSelectionPage />,
        },
        {
            path: RoutePath.getEditAlbumPath(':albumIDB64', ':goToAfter'),
            element: <LoggedInEditAlbumPage />,
        },
        {
            path: RoutePath.Documents,
            element: <LoggedInDocumentsPage />,
        },
        {
            path: RoutePath.Settings,
            element: <LoggedInSettingsPage />,
        },
        {
            path: `${RoutePath.Settings}/testFlags`,
            element: <TestFlagAdmin />,
        },
        {
            path: RoutePath.SettingsStorage,
            element: <LoggedInStoragePage />,
        },
        {
            path: RoutePath.SettingsPrivacy,
            element: <LoggedInPrivacyPage />,
        },
        {
            path: RoutePath.SettingsTakeout,
            element: <LoggedInTakeoutPage />,
        },
        {
            path: RoutePath.SettingsUnsubscribe,
            element: <Unsubscribe />,
        },
        {
            path: RoutePath.InAppPrivacySettings,
            element: <LoggedInPrivacyPage />,
        },
        {
            path: RoutePath.SettingsLoggedInDevices,
            element: <LoggedInLoggedInDevicesPage />,
        },
        {
            path: RoutePath.SelectProfilePicture,
            element: <LoggedInSelectProfilePicturePage />,
        },
        {
            path: `${RoutePath.Settings}/_advanced`,
            element: <LoggedInAdvancedSettings />,
        },
        {
            path: RoutePath.getAlbumImageAsSharePath(':albumIDB64', ':fileID'),
            element: <MaybeLoggedInAlbumFileAsShareMount />,
        },
        {
            path: RoutePath.getNotLoggedInAlbumPath(':albumIDB64'),
            element: <MaybeLoggedInAlbumMount />,
        },
        {
            path: RoutePath.getAlbumCarouselPath(':albumIDB64', ':fileID'),
            loader: albumCarouselLoader,
            element: <MaybeLoggedInAlbumCarouselViewPage />,
        },
        {
            path: RoutePath.getCreateAlbumLoginPath(':albumIDB64'),
            element: <MaybeLoggedInRequireLoginCreateNewAlbum />,
        },
        {
            path: RoutePath.getCopyAlbumLoginPath(':albumIDB64'),
            element: <MaybeLoggedInLoginRequiredCopyAlbum />,
        },
        {
            path: RoutePath.getCopyAlbumFileLoginPath(':albumIDB64', ':fileID'),
            element: <MaybeLoggedInLoginRequiredCopyAlbum />,
        },
        {
            path: RoutePath.getSubscribeToAlbumLoginPath(':albumIDB64'),
            element: <MaybeLoggedInRequireLoginSubscribeToAlbum />,
        },
        {
            path: RoutePath.getShareReceiverPath(':shareID'),
            element: <MaybeLoggedInShareMountUnpackedUUID />,
        },
        {
            path: RoutePath.getCopyShareLoginPath(':shareID'),
            element: <MaybeLoggedInRequireLoginCopyShare />,
        },
        {
            path: RoutePath.getAlbumFileCommentsPath(':albumIDB64', ':fileID'),
            element: <MaybeLoggedInCommentsListOverlay />,
        },
        {
            path: RoutePath.EmailSettings,
            element: <MaybeLoggedInEmailSettings />,
        },
        {
            path: RoutePath.getErrorPath(':error'),
            element: <MaybeLoggedInErrorPageMount />,
        },
        {
            path: '*',
            element: <MaybeLoggedInPageNotFound />,
        },
    ],
    {
        basename: import.meta.env.BASE_URL,
    },
)

export const MainRouter: React.FunctionComponent = () => (
    <Suspense fallback={<LoadingPage />}>
        <RouterProvider router={router} />
    </Suspense>
)
